import React, { FC } from 'react';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment, { Moment } from 'moment';
import MuiTooltip from '@mui/material/Tooltip';
import translate from '@translate';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { ClearIcon, EndIconTypeProps, getEndAdornment, StyledTextField } from '../TextField';
import CloseIcon from '@mui/icons-material/Close';
import { TextFieldProps } from '@mui/material';
import { LockIcon, WarningIcon } from '../SkladonIcon/SmallIcons';
import { getDateFormat, getDateTimeFormat, shortDateFormat } from 'src/utils/timeConstants';

export type PropValueType = Moment | Date | null | string | undefined;
export interface DateTimePickerPropsType {
    value?: PropValueType;
    error?: boolean;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    disablePast?: boolean;
    disableFuture?: boolean;
    label?: any;
    name?: string;
    format?: string;
    onChange?: (value: PropValueType, error: boolean) => void;
    onError?: (error: any, value: any) => void;
    shouldDisableDate?: (value: Date | Moment | string) => boolean;
    minDate?: string | Moment | undefined;
    maxDate?: string | Moment | undefined;
    helperText?: string;
    tooltip?: string;
    type: 'date' | 'dateTime' | 'time';
}

const DateTimePicker: FC<DateTimePickerPropsType> = (props) => {
    const {
        value,
        onChange,
        error,
        required,
        disabled,
        fullWidth,
        label,
        name,
        tooltip,
        helperText,
        format,
        minDate,
        maxDate,
        disablePast,
        disableFuture,
        type,
        shouldDisableDate,
        onError
    } = props;

    const handleChange = (newValue: PropValueType) => {
        if (onChange) {
            let newError = !newValue && !!required;
            if (moment.isMoment(newValue)) {
                if (!newValue.isValid()) {
                    newError = true;
                } else if (disableFuture && newValue.diff(moment()) > 0) {
                    newError = true;
                } else if (disablePast && newValue.diff(moment()) < 0) {
                    newError = true;
                }
            }

            onChange(newValue, newError);
        }
    };

    // const handleError = (reason: DateTimeValidationError, newValue: string | Moment | null | undefined) => {
    //     console.log('handleError', reason, newValue);
    //     if (onChange) {
    //         onChange(newValue, !!reason);
    //     }
    // };

    let errorText = '';
    if (error) {
        if (required) {
            errorText = translate('textField.requiredError');
        } else {
            errorText = translate('textField.error');
        }
    }

    const updateInputParams = (params: TextFieldProps): TextFieldProps => {
        let endIconParams: EndIconTypeProps | null = null;
        if (error) {
            endIconParams = { icon: <WarningIcon /> };
        } else if (disabled) {
            endIconParams = { icon: <LockIcon /> };
        }
        params.InputProps = {
            ...params.InputProps,
            endAdornment: (
                <>
                    {params.InputProps?.endAdornment}
                    {!disabled && (
                        <ClearIcon className="number-filter-clear" onClick={() => handleChange(null)} size="medium">
                            <CloseIcon />
                        </ClearIcon>
                    )}
                    {endIconParams && getEndAdornment(endIconParams, disabled)}
                </>
            ),
        };
        if (params?.inputProps?.placeholder) {
            params.inputProps.placeholder = '';
        }
        return params;
    };

    const renderHiddenInput = () => {
        let inputVal: string | Date = '';
        if (value) {
            if (moment.isMoment(value)) {
                inputVal = value.format(type === 'time' ? 'HH:mm' : 'YYYY-MM-DD');
            } else {
                inputVal = moment(value).format(type === 'time' ? 'HH:mm' : 'YYYY-MM-DD');
            }
        }
        return (
            <input
                name={name}
                value={inputVal}
                required={required}
                disabled
                style={{ display: 'none' }}
                type={type === 'time' ? 'time' : 'date'}
            />
        );
    };

    const getPicker = () => {
        switch (type) {
            case 'date':
                return (
                    <DatePicker
                        label={label}
                        openTo="day"
                        value={value || null}
                        onChange={handleChange}
                        onError={onError}
                        disabled={disabled}
                        readOnly={disabled}
                        inputFormat={format || getDateFormat()}
                        minDate={minDate}
                        maxDate={maxDate}
                        disablePast={disablePast}
                        disableFuture={disableFuture}
                        shouldDisableDate={shouldDisableDate}
                        renderInput={(params) => {
                            const updatedParams = updateInputParams(params);
                            return (
                                <StyledTextField
                                    {...updatedParams}
                                    fullWidth={fullWidth}
                                    required={required}
                                    error={error}
                                    helperText={errorText || helperText || ' '}
                                    InputLabelProps={{ style: { pointerEvents: "auto" }, shrink: true }}
                                />
                            );
                        }}
                    />
                );
            case 'time':
                return (
                    <TimePicker
                        label={label}
                        value={value || null}
                        onChange={handleChange}
                        // onError={handleError}
                        disabled={disabled}
                        readOnly={disabled}
                        ampm={false}
                        inputFormat={format || 'HH:mm'}
                        renderInput={(params) => {
                            const updatedParams = updateInputParams(params);
                            return (
                                <StyledTextField
                                    {...updatedParams}
                                    fullWidth={fullWidth}
                                    required={required}
                                    error={error}
                                    helperText={errorText || helperText || ' '}
                                />
                            );
                        }}
                    />
                );
            default:
                return (
                    <MuiDateTimePicker
                        label={label}
                        openTo="day"
                        value={value || null}
                        onChange={handleChange}
                        // onError={handleError}
                        disabled={disabled}
                        readOnly={disabled}
                        ampm={false}
                        inputFormat={format || getDateTimeFormat()}
                        minDate={minDate}
                        maxDate={maxDate}
                        disablePast={disablePast}
                        disableFuture={disableFuture}
                        shouldDisableDate={shouldDisableDate}
                        renderInput={(params) => {
                            const updatedParams = updateInputParams(params);
                            return (
                                <StyledTextField
                                    {...updatedParams}
                                    fullWidth={fullWidth}
                                    required={required}
                                    error={error}
                                    helperText={errorText || helperText || ' '}
                                    InputLabelProps={{ style: { pointerEvents: "auto" }, shrink: true }}
                                />
                            );
                        }}
                    />
                );
        }
    };

    const pickerBlock = (
        <>
            {getPicker()}
            {renderHiddenInput()}
        </>
    );

    if (tooltip) {
        return <MuiTooltip title={tooltip}>{pickerBlock}</MuiTooltip>;
    }

    return pickerBlock;
};

export default DateTimePicker;
